import { Edge, Node } from "reactflow";
import { getMacroChannelLabel } from "../../../constants/MacroChannels";
import { ActionTypeBackend } from "../macroNode.types";
import { MacroStatus } from "../macroNodeManager";
import { NodeSubType } from "../../../models/nodeSubType";

export interface ValidateMacroResult {
  isValid: boolean;
  errorMessage: string;
  status: MacroStatus;
  isResponseIncluded: boolean;
  validationErrorPayload: any;
  errorsCount: number;
}

/**
 * Validates the Macro flow using DFS, mirroring the layout order.
 * Enforces DFS depth limits, reachability, cycle detection, and consistency.
 */
export function validateMacroWithDFS(
  nodes: Node[],
  edges: Edge[],
  flowMeta?: { [key: string]: any }
): ValidateMacroResult {
  if (!nodes || nodes.length === 0) {
    return {
      isValid: false,
      errorMessage: "Macro is missing nodes.",
      status: MacroStatus.ARCHIVED,
      isResponseIncluded: false,
      validationErrorPayload: {},
      errorsCount: 0,
    };
  }

  let errorsCount = 0;
  const validationErrorPayload: any = {
    combined_errors: {},
  };

  const addError = (nodeId: string, error: string) => {
    const node = nodes.find((n) => n.id === nodeId);
    const nodeName = node?.data?.nodeData?.name || `Block ${nodeId}`;

    if (!validationErrorPayload[nodeId]) {
      validationErrorPayload[nodeId] = { errors: [] };
    }
    if (!validationErrorPayload[nodeId].errors.includes(error)) {
      validationErrorPayload[nodeId].errors.push(error);
      validationErrorPayload.combined_errors[errorsCount] = {
        node_names: [nodeName],
        error: error,
      };
      errorsCount++;
    }
  };

  const backendNodes: { [key: string]: any } = {};
  nodes.forEach((n) => {
    backendNodes[n.id] = n.data?.nodeData?.backendConfig || {};
  });

  const activeChannels: string[] = backendNodes["N1"]?.channels || ["whatsapp"];
  let isResponseIncluded = false;
  const messageBlocksPerChannel: { [channel: string]: number } = {};
  activeChannels.forEach((ch) => {
    messageBlocksPerChannel[ch] = 0;
  });

  // Adjacency list for DFS — stores { target, sourceHandle } so we can
  // determine which channel edge was followed from the trigger node.
  const layoutableIds = new Set(nodes.map((n) => n.id));
  const adjList = new Map<string, { target: string; sourceHandle: string }[]>();
  layoutableIds.forEach((id) => adjList.set(id, []));

  edges.forEach((e) => {
    if (layoutableIds.has(e.source) && layoutableIds.has(e.target)) {
      adjList.get(e.source)?.push({
        target: e.target,
        sourceHandle: e.sourceHandle || "source",
      });
    }
  });

  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const genericHandles = new Set(["source", "target", "fallback"]);
  const allVisitedNodes = new Set<string>();

  /**
   * @param nodeId  – current node being visited
   * @param depth   – current depth in the traversal (0 = N1)
   * @param channel – inherited channel context from the N1 trigger edge
   *                  (e.g. "whatsapp", "email", "instagram_dm").
   *                  undefined when traversing from a non-channel edge (fallback, standard next).
   */
  const dfs = (nodeId: string, depth: number, channel?: string) => {
    const visitKey = `${nodeId}:${channel || "none"}`;
    // Check 6: Cycle detection (use node-only for cycle, not channel-specific)
    if (recursionStack.has(nodeId)) {
      addError(nodeId, "Cycle detected: Flow contains an infinite loop.");
      return;
    }
    // Check if this node was already validated for this specific channel context
    if (visited.has(visitKey)) {
      return;
    }

    // Check 1: Depth > 29 (i.e. > 30 blocks in a single path)
    if (depth > 29) {
      addError(nodeId, "Macro has more than 30 blocks.");
      return; // Stop traversal for this path
    }

    visited.add(visitKey);
    allVisitedNodes.add(nodeId);
    recursionStack.add(nodeId);

    const nodeBackend = backendNodes[nodeId];
    if (nodeBackend) {
      const type = nodeBackend.type?.toLowerCase();

      if (type === ActionTypeBackend.MESSAGE.toLowerCase()) {
        isResponseIncluded = true;

        const blockChannel =
          nodeBackend.channel || nodeBackend.automationChannel;
        if (blockChannel) {
          // Validate channel matches inherited context from N1
          if (channel && blockChannel !== channel) {
            addError(
              nodeId,
              `Response block channel '${getMacroChannelLabel(blockChannel)}' does not match the connected trigger channel '${getMacroChannelLabel(channel)}'.`
            );
          }
          if (!activeChannels.includes(blockChannel)) {
            addError(
              nodeId,
              `Response block uses channel '${getMacroChannelLabel(blockChannel)}' which is not enabled in the Starting Point.`
            );
          }
        }

        if (
          !nodeBackend.message &&
          nodeBackend.subType !== NodeSubType.TICKET_FORM
        ) {
          addError(nodeId, `Response block is empty.`);
        }
      } else if (type === ActionTypeBackend.MULTI_ACTION.toLowerCase()) {
        if (nodeBackend.subType === NodeSubType.TICKET_FORM) {
          if (!nodeBackend.configuration?.formId) {
            addError(nodeId, `Ticket Form block is not fully configured.`);
          }
        } else if (
          !nodeBackend.actions ||
          Object.keys(nodeBackend.actions).length === 0
        ) {
          addError(nodeId, `Action block is not fully configured.`);
        }
      } else if (type === ActionTypeBackend.START_A_FLOW.toLowerCase()) {
        if (!nodeBackend.config || !nodeBackend.config.flowId) {
          addError(nodeId, `Connect to flow block is not fully configured.`);
        }
      }
    }

    // Traverse children, propagating channel context
    const neighbors = adjList.get(nodeId) || [];
    for (const edge of neighbors) {
      // Determine channel for next node:
      // - At N1 trigger: sourceHandle IS the channel name (whatsapp, email, etc.)
      //   unless it's a generic handle like "source", "target", or "fallback"
      // - At all other nodes: inherit the channel from the parent
      let childChannel = channel; // default: inherit from parent

      const nodeType = nodeBackend?.type?.toLowerCase();
      if (nodeType === "trigger" && !genericHandles.has(edge.sourceHandle)) {
        // N1's channel-specific edge → the sourceHandle is the channel
        childChannel = edge.sourceHandle;
      }

      dfs(edge.target, depth + 1, childChannel);
    }

    recursionStack.delete(nodeId);
  };
  dfs("N1", 0);

  // Single-pass: Count message blocks per channel and track which nodes belong to each channel
  const channelToNodes = new Map<string, string[]>();

  for (const nodeId of allVisitedNodes) {
    const nodeBack = backendNodes[nodeId];
    if (nodeBack?.type?.toLowerCase() === "message") {
      const blockChannel = nodeBack.channel || nodeBack.automationChannel;
      if (blockChannel && activeChannels.includes(blockChannel)) {
        if (!channelToNodes.has(blockChannel)) {
          channelToNodes.set(blockChannel, []);
        }
        channelToNodes.get(blockChannel)!.push(nodeId);
      }
    }
  }

  // Validate each active channel (single loop over channels, not nodes)
  for (const channel of activeChannels) {
    const nodeIds = channelToNodes.get(channel) || [];
    const count = nodeIds.length;

    if (count > 1) {
      // Multiple response blocks for this channel - error on each block
      for (const nodeId of nodeIds) {
        addError(
          nodeId,
          `Only one Response block is allowed per channel (${getMacroChannelLabel(channel)}). Found ${count} blocks.`
        );
      }
    }

    messageBlocksPerChannel[channel] = count;
  }

  // Check: Max 30 blocks connected to N1
  if (allVisitedNodes.size > 30) {
    addError("N1", "A macro can have a maximum of 30 blocks.");
  }

  // --- Post-DFS: Validate startAflow channel consistency ---
  // Single multi-source BFS to find which channels reach each node.
  // This avoids the DFS visited-set limitation for converging branches.
  if (flowMeta) {
    const nodeChannels = new Map<string, Set<string>>();
    const n1Edges = adjList.get("N1") || [];

    // Multi-source BFS: Initialize queue with all channel edges from N1
    const queue: { nodeId: string; channel: string }[] = [];
    for (const edge of n1Edges) {
      if (!genericHandles.has(edge.sourceHandle)) {
        queue.push({ nodeId: edge.target, channel: edge.sourceHandle });
      }
    }
    // Optimize BFS with index-based iteration instead of shift()
    const seen = new Set<string>();
    let queueIndex = 0;
    while (queueIndex < queue.length) {
      const { nodeId: current, channel: ch } = queue[queueIndex++];

      const seenKey = `${current}:${ch}`;
      if (seen.has(seenKey)) continue;
      seen.add(seenKey);

      if (!nodeChannels.has(current)) nodeChannels.set(current, new Set());
      nodeChannels.get(current)!.add(ch);

      for (const e of adjList.get(current) || []) {
        const nextSeenKey = `${e.target}:${ch}`;
        if (!seen.has(nextSeenKey)) {
          queue.push({ nodeId: e.target, channel: ch });
        }
      }
    }

    // Check each startAflow node (only visited nodes) against all channels that reach it
    for (const nodeId of allVisitedNodes) {
      const nodeBack = backendNodes[nodeId];
      if (
        nodeBack?.type?.toLowerCase() ===
          ActionTypeBackend.START_A_FLOW.toLowerCase() &&
        nodeChannels.has(nodeId)
      ) {
        const connectedFlowId = nodeBack.config?.flowId;
        const connectedFlowMeta = connectedFlowId
          ? flowMeta[connectedFlowId]
          : nodeBack.config?.flowChannel;
        const connectedFlowChannel = connectedFlowMeta?.channel;
        if (connectedFlowChannel) {
          for (const ch of nodeChannels.get(nodeId)!) {
            if (ch !== connectedFlowChannel) {
              addError(
                nodeId,
                `Connected flow is for '${getMacroChannelLabel(connectedFlowChannel)}' but this branch is '${getMacroChannelLabel(ch)}'.`
              );
            }
          }
        }
      }
    }
  }

  const isValid = errorsCount === 0;

  return {
    isValid,
    errorMessage: isValid ? "" : "Macro has validation errors.",
    status: isValid ? MacroStatus.LIVE : MacroStatus.ARCHIVED,
    isResponseIncluded,
    validationErrorPayload,
    errorsCount,
  };
}
